import { moment } from '@eventbrite/date';
import {
    constants as datetimeConstants,
    getFormattedDateTime,
    getFormattedTimezone,
} from '@eventbrite/datetime';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import momentTimezone from 'moment-timezone';

const {
    DEFAULT_TIME_FORMAT,
    DEFAULT_TIMEZONE,
    DEFAULT_LANGUAGE,
    DEFAULT_DATETIME_FORMAT,
} = datetimeConstants;

interface EventbriteEvent {
    start: {
        utc: string;
        timezone: string;
    };
    end: {
        utc: string;
    };
    hideStartDate?: boolean;
    hideEndDate?: boolean;
    listingDisplayFlags?: { shouldShowTimezone?: boolean };
}

/**
 * getFormattedDateTimeRange
 *
 * Returns a string representing a datetime range formatted for a given
 * timezone. If no timezone is provided, use the default timezone. If showTimezone
 * is false, do not display the timezone as part of the range.
 *
 * Note: In the future, instead of a default timezone it may be better to use
 * the browser's timezone. Moment Timezone 0.5.0 has a function momentTimezone.tz.guess(),
 * which we could use if we upgrade the library. (EB-43817)
 *
 * @return An event's datetime range
 */
export const getFormattedDateTimeRange = (
    event: Pick<EventbriteEvent, 'start' | 'end' | 'listingDisplayFlags'>,
    locale = DEFAULT_LANGUAGE,
    dateTimeFormat = DEFAULT_DATETIME_FORMAT,
): string => {
    const startDateTime = get(event, 'start.utc');
    const endDateTime = get(event, 'end.utc');
    const timezone = get(event, 'start.timezone', DEFAULT_TIMEZONE);
    const showTimezone = get(
        event,
        'listingDisplayFlags.shouldShowTimezone',
        true,
    );
    const startDateTimeAsMoment = momentTimezone
        .tz(startDateTime, timezone)
        .locale(locale);
    const endDateTimeAsMoment = momentTimezone
        .tz(endDateTime, timezone)
        .locale(locale);

    // If the two dates are on the same day in the given timezone, the end datetime
    // should be formatted to include only the time to avoid printing the same
    // information twice
    const endDateTimeFormat = startDateTimeAsMoment.isSame(
        endDateTimeAsMoment,
        'day',
    )
        ? DEFAULT_TIME_FORMAT
        : dateTimeFormat;
    const formattedStartDateTime = startDateTimeAsMoment.format(dateTimeFormat);
    const formattedEndDateTime = endDateTimeAsMoment.format(endDateTimeFormat);

    const localizedLazyString = showTimezone
        ? // 'Tue, Feb 28, 2017 7:00 PM - Sat, Mar 4, 2017 7:00 PM EST'
          gettext(
              '%(formattedStartDateTime)s - %(formattedEndDateTime)s %(timezone)s',
              {
                  formattedStartDateTime,
                  formattedEndDateTime,
                  timezone: getFormattedTimezone(timezone, startDateTime),
              },
          )
        : // 'Tue, Feb 28, 2017 7:00 PM - Sat, Mar 4, 2017 7:00 PM'
          gettext('%(formattedStartDateTime)s - %(formattedEndDateTime)s', {
              formattedStartDateTime,
              formattedEndDateTime,
          });

    return localizedLazyString.toString();
};

const _getStartDayAndTime = (
    startDayOfTheWeek: string,
    startTime: string,
    formattedTimezone: string,
) =>
    // 'Sun, 7:00 PM PDT' or
    // 'Sun, 7:00 PM'
    gettext('%(startDayOfTheWeek)s, %(startTime)s %(formattedTimezone)s', {
        startDayOfTheWeek,
        startTime,
        formattedTimezone,
    }).toString();

const _getDayAndTimeMultiDayRange = (
    startDayOfTheWeek: string,
    startTime: string,
    endDayOfTheWeek: string,
    endTime: string,
    formattedTimezone: string,
) =>
    // 'Fri, 9:00 PM - Sat, 2:00 AM PDT' or
    // 'Fri, 9:00 PM - Sat, 2:00 AM'
    gettext(
        '%(startDayOfTheWeek)s, %(startTime)s - %(endDayOfTheWeek)s, %(endTime)s %(formattedTimezone)s',
        {
            startDayOfTheWeek,
            startTime,
            endDayOfTheWeek,
            endTime,
            formattedTimezone,
        },
    ).toString();

const _getSingleDayAndTimeRange = (
    startDayOfTheWeek: string,
    startTime: string,
    endTime: string,
    formattedTimezone: string,
) =>
    // 'Sun, 7:00 PM - 10:00 PM PDT' or
    // 'Sun, 7:00 PM - 10:00 PM'
    gettext(
        '%(startDayOfTheWeek)s, %(startTime)s - %(endTime)s %(formattedTimezone)s',
        {
            startDayOfTheWeek,
            startTime,
            endTime,
            formattedTimezone,
        },
    );

/**
 * Returns a localized day of the week and time or time range to display
 * for each child event on the repeating event parent page.
 * For instance:
 * (may include timezone or not)
 * Tue, 7:00 PM PST
 * Tue, 7:00 PM - 9:00 PM PST
 * Fri, 9:00 PM - Sat, 2:00 AM PST
 * ven., 21:00 - sam., 02:00 CET
 */
export const getFormattedChildEventDayAndTime = ({
    startDateTime,
    endDateTime,
    timezone,
    locale,
    hideEndDate = false,
    showTimezone = true,
    shouldHideSeriesStartTime = false,
}: {
    startDateTime: string;
    endDateTime: string;
    timezone: string;
    locale: string;
    hideEndDate?: boolean;
    showTimezone?: boolean;
    shouldHideSeriesStartTime?: boolean;
}) => {
    let formattedDayAndTime: string | GenericLazyString = '';

    const startDateTimeAsMoment = moment(startDateTime).locale(locale);
    const endDateTimeAsMoment = moment(endDateTime).locale(locale);

    const isMultiDayEvent = !startDateTimeAsMoment.isSame(
        endDateTimeAsMoment,
        'day',
    );

    const startDayOfTheWeek = startDateTimeAsMoment.format('ddd');
    const endDayOfTheWeek = isMultiDayEvent
        ? endDateTimeAsMoment.format('ddd')
        : '';

    const startTime = startDateTimeAsMoment.format(DEFAULT_TIME_FORMAT);
    const endTime = endDateTimeAsMoment.format(DEFAULT_TIME_FORMAT);

    const formattedTimezone = showTimezone
        ? getFormattedTimezone(timezone, startDateTime)
        : '';

    if (shouldHideSeriesStartTime) {
        // only show the start day of the week, but use the full day rather
        // than the abbreviation, e.g. 'Tuesday'
        formattedDayAndTime = startDateTimeAsMoment.format('dddd');
    } else if (hideEndDate) {
        formattedDayAndTime = _getStartDayAndTime(
            startDayOfTheWeek,
            startTime,
            formattedTimezone,
        );
    } else if (isMultiDayEvent) {
        formattedDayAndTime = _getDayAndTimeMultiDayRange(
            startDayOfTheWeek,
            startTime,
            endDayOfTheWeek,
            endTime,
            formattedTimezone,
        );
    } else {
        formattedDayAndTime = _getSingleDayAndTimeRange(
            startDayOfTheWeek,
            startTime,
            endTime,
            formattedTimezone,
        );
    }

    // must trim to remove trailing space if !showTimezone
    return formattedDayAndTime.trimEnd();
};

/**
 * Returns the full event date range string to display in the subtitle on the modal
 * and purchase confirmation page
 */
export const getFormattedEventDate = (
    event: EventbriteEvent | undefined,
    locale?: string,
): string => {
    if (!event || isEmpty(event)) {
        return '';
    }

    const {
        listingDisplayFlags: { shouldShowTimezone } = {},
        hideStartDate,
        hideEndDate,
    } = event;

    if (hideStartDate && hideEndDate) {
        return shouldShowTimezone
            ? getFormattedTimezone(event.start.timezone)
            : '';
    }

    if (hideStartDate) {
        // 'Ends on Sun, Apr 14, 2019 10:00 PM PDT'
        return gettext('Ends on %(datetime)s', {
            datetime: getFormattedDateTime(
                event.end.utc,
                shouldShowTimezone,
                event.start.timezone,
                locale,
            ),
        }).toString();
    }

    if (hideEndDate) {
        // 'Starts on Sun, Apr 14, 2019 7:00 PM PDT'
        return gettext('Starts on %(datetime)s', {
            datetime: getFormattedDateTime(
                event.start.utc,
                shouldShowTimezone,
                event.start.timezone,
                locale,
            ),
        }).toString();
    }

    // 'Sun, Apr 14, 2019 7:00 PM - 10:00 PM PDT'
    return getFormattedDateTimeRange(
        event,
        locale,
        DEFAULT_DATETIME_FORMAT,
    ).toString();
};

/**
 * Returns the event payment due date range string
 * to display on the cash based payment method's purchase confirmation page
 */
export const getFormattedDueDate = (
    _event: EventbriteEvent,
    locale: string,
    deferredPaymentDueDays: number,
) => {
    const dueDateFormat = 'LL';
    const dueDate = moment().add(deferredPaymentDueDays, 'day');

    return getFormattedDateTime(dueDate, false, null, locale, dueDateFormat);
};
